import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { displayFieldValue } from '../../lib/custom-field-display';
import {
  daysSince,
  formatDate,
  formatDateTime,
  formatMoney,
  formatRelative,
} from '../../lib/format';
import {
  chatApi,
  addLeadNote,
  customFieldGroups,
  leadAddresses,
  leadRelations,
  listLeadActivities,
  listLeadBusinesses,
  listLeadHistory,
  setLeadField,
  updateLead,
  type LeadPayload,
} from '../../lib/resources';
import type {
  HierarchyNode,
  HistoryCategory,
  Lead,
  LeadAddressRow,
  LeadAddressType,
  RelationKind,
} from '../../types';
import { ActivityComposer, ActivityRow } from '../ActivityKit';
import { BusinessFiles } from '../BusinessFiles';
import { CustomFieldInput } from '../CustomFieldInput';
import { InlineEdit } from '../InlineEdit';
import { LeadPicker } from '../LeadPicker';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  Modal,
  StatusBadge,
  Tabs,
  Toggle,
  inputClass,
} from '../ui';
import { LeadBusinessModal } from './LeadBusinessModal';
import { useLeadRefresh } from '../../lib/use-lead-refresh';

type UpdatePayload = Parameters<typeof updateLead>[1];

/** Gravar um campo da ficha (o ✓) e atualizar o que mostra o lead. */
function useSaveField(lead: Lead) {
  const refresh = useLeadRefresh(lead.id);
  return async (payload: UpdatePayload) => {
    await updateLead(lead.id, payload);
    refresh();
  };
}

function useCanWrite() {
  return useAuth().can('leads_admin', 'leads_member_operator');
}

// ------------------------------------------------------------------- Perfil

export function ProfileSection({ lead }: { lead: Lead }) {
  const save = useSaveField(lead);
  const canWrite = useCanWrite();
  const isCompany = lead.kind === 'COMPANY';
  const text = (key: keyof LeadPayload) => (value: string | null) =>
    save({ [key]: value } as UpdatePayload);
  const profileFields = (lead.additionalFields ?? []).filter((field) => field.showOnProfile);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <h3 className="mb-1 text-sm font-semibold text-slate-700">Dados cadastrais</h3>
        <InlineEdit
          disabled={!canWrite}
          label={isCompany ? 'Razão social' : 'Nome'}
          value={lead.name}
          onSave={(value) => save({ name: value ?? lead.name })}
        />
        {isCompany ? (
          <InlineEdit
            disabled={!canWrite}
            label="Nome fantasia"
            value={lead.tradeName}
            onSave={text('tradeName')}
          />
        ) : (
          <InlineEdit
            disabled={!canWrite}
            label="Nome de exibição"
            value={lead.displayName}
            onSave={text('displayName')}
          />
        )}
        <InlineEdit
          disabled={!canWrite}
          label="Origem"
          value={lead.source}
          onSave={text('source')}
        />
        <InlineEdit
          disabled={!canWrite}
          label="Site"
          type="url"
          value={lead.site}
          display={
            lead.site ? (
              <a
                href={lead.site}
                target="_blank"
                rel="noreferrer"
                className="text-sky-700 underline"
              >
                {lead.site}
              </a>
            ) : undefined
          }
          onSave={text('site')}
        />
        <InlineEdit
          disabled={!canWrite}
          label="Instagram"
          value={lead.instagram}
          onSave={text('instagram')}
        />
        {isCompany ? (
          <InlineEdit
            disabled={!canWrite}
            label="Data de fundação"
            type="date"
            value={lead.foundationDate}
            display={formatDate(lead.foundationDate)}
            onSave={text('foundationDate')}
          />
        ) : (
          <InlineEdit
            disabled={!canWrite}
            label="Data de nascimento"
            type="date"
            value={lead.birthDate}
            display={formatDate(lead.birthDate)}
            onSave={text('birthDate')}
          />
        )}
        <InlineEdit
          disabled={!canWrite}
          label="Documento"
          value={lead.taxId}
          onSave={text('taxId')}
        />
        {isCompany ? (
          <InlineEdit
            disabled={!canWrite}
            label="Segmento"
            value={lead.segment}
            onSave={text('segment')}
          />
        ) : (
          <InlineEdit
            disabled={!canWrite}
            label="Cargo"
            value={lead.jobTitle}
            onSave={text('jobTitle')}
          />
        )}
        {profileFields.map((field) => (
          <InlineEdit
            key={field.id}
            disabled={!canWrite}
            label={field.name}
            value={field.value}
            display={displayFieldValue(field, field.value)}
            onSave={async (value) => {
              await setLeadField(lead.id, field.id, value);
              await save({});
            }}
            renderInput={(draft, setDraft) => (
              <CustomFieldInput field={field} value={draft} onChange={setDraft} />
            )}
          />
        ))}
      </div>
      <div>
        <h3 className="mb-1 text-sm font-semibold text-slate-700">Contatos</h3>
        <InlineEdit
          disabled={!canWrite}
          label="E-mail"
          type="email"
          value={lead.email}
          onSave={text('email')}
        />
        <InlineEdit
          disabled={!canWrite}
          label="Telefone"
          type="tel"
          value={lead.phone}
          onSave={text('phone')}
        />
        {isCompany ? (
          <div className="mt-4">
            <h3 className="mb-1 text-sm font-semibold text-slate-700">Contato principal</h3>
            {lead.primaryContact ? (
              <p className="text-sm text-slate-700">
                {lead.primaryContact.name}
                {lead.primaryContact.jobTitle && (
                  <span className="text-slate-400"> · {lead.primaryContact.jobTitle}</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-slate-400">
                Nenhum contato principal — vincule em Pessoas.
              </p>
            )}
            {lead.parentCompany && (
              <p className="mt-2 text-xs text-slate-500">Matriz: {lead.parentCompany.name}</p>
            )}
          </div>
        ) : (
          lead.companies.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-1 text-sm font-semibold text-slate-700">Empresas</h3>
              {lead.companies.map((company) => (
                <p key={company.relationId} className="text-sm text-slate-700">
                  {company.name}
                  {company.jobTitle && (
                    <span className="text-slate-400"> · {company.jobTitle}</span>
                  )}
                </p>
              ))}
            </div>
          )
        )}
        {lead.company && (
          <p className="mt-4 text-xs text-slate-400">
            Empresa (campo antigo, texto): {lead.company}
          </p>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------- Campos adicionais

export function CustomFieldsSection({ lead }: { lead: Lead }) {
  const canWrite = useCanWrite();
  const refresh = useLeadRefresh(lead.id);
  const [term, setTerm] = useState('');
  const scope = lead.kind === 'COMPANY' ? 'company' : 'lead';
  const groupsQuery = useQuery({
    queryKey: ['custom-field-groups', 'lead-sheet', scope],
    queryFn: async () => {
      const [person, company] = await Promise.all([
        customFieldGroups.list('lead'),
        scope === 'company'
          ? customFieldGroups.list('company')
          : Promise.resolve({ count: 0, data: [] }),
      ]);
      return [...person.data, ...company.data];
    },
  });

  const fields = (lead.additionalFields ?? []).filter((field) =>
    field.name.toLowerCase().includes(term.trim().toLowerCase()),
  );
  const groups = groupsQuery.data ?? [];
  const sections = [
    ...groups.map((group) => ({
      group,
      items: fields.filter((field) => field.groupId === group.id),
    })),
    {
      group: null,
      items: fields.filter((field) => !groups.some((group) => group.id === field.groupId)),
    },
  ].filter((section) => section.items.length > 0);

  if ((lead.additionalFields ?? []).length === 0) {
    return (
      <EmptyState
        title="Nenhum campo adicional"
        hint="Cadastre em Configurações › Campos adicionais."
      />
    );
  }

  return (
    <div className="space-y-4">
      <input
        className={`${inputClass} mt-0 max-w-xs`}
        placeholder="Pesquisar…"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      {sections.map(({ group, items }) => (
        <div key={group?.id ?? 'sem-grupo'} className="rounded-xl border border-slate-200 p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
            {group?.color && (
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: group.color }}
              />
            )}
            {group?.name ?? 'Sem grupo'}
          </p>
          <div className={`grid gap-x-6 ${group?.columns === 2 ? 'md:grid-cols-2' : ''}`}>
            {items.map((field) => (
              <InlineEdit
                key={field.id}
                disabled={!canWrite}
                label={field.name}
                value={field.value}
                display={displayFieldValue(field, field.value)}
                onSave={async (value) => {
                  await setLeadField(lead.id, field.id, value);
                  refresh();
                }}
                renderInput={(draft, setDraft) => (
                  <CustomFieldInput field={field} value={draft} onChange={setDraft} />
                )}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ Negócios

export function BusinessesSection({ lead }: { lead: Lead }) {
  const queryClient = useQueryClient();
  const canWrite = useAuth().can('pipeline_admin', 'pipeline_member');
  const [scope, setScope] = useState<'lead' | 'company' | 'both'>('lead');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['lead-businesses', lead.id, scope, status, search, dateFrom, dateTo],
    queryFn: () =>
      listLeadBusinesses(lead.id, {
        scope,
        status,
        search: search || undefined,
        dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
        dateTo: dateTo ? new Date(`${dateTo}T23:59:59`).toISOString() : undefined,
      }),
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[
          ['Ticket médio', formatMoney(lead.metrics.averageTicket)],
          ['Total', formatMoney(lead.metrics.totalSpent)],
          [
            'Ciclo de compra',
            lead.metrics.purchaseFrequency
              ? `${Math.round(lead.metrics.purchaseFrequency)} dias`
              : '—',
          ],
          [
            'Última compra',
            lead.metrics.lastPurchaseDate
              ? `há ${daysSince(lead.metrics.lastPurchaseDate)} dias`
              : '—',
          ],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-slate-200 p-3">
            <p className="text-[11px] text-slate-400">{label}</p>
            <p className="text-sm font-semibold text-slate-800">{value}</p>
          </div>
        ))}
      </div>

      <Tabs
        tabs={[
          ['lead', 'Lead'],
          ['company', 'Empresa'],
          ['both', 'Lead + Empresa'],
        ]}
        value={scope}
        onChange={setScope}
      />
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={`${inputClass} mt-0 w-48`}
          placeholder="Pesquisar..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <select
          className={`${inputClass} mt-0 w-36`}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">Todas</option>
          <option value="in_process">Em aberto</option>
          <option value="won">Ganho</option>
          <option value="lost">Perdido</option>
        </select>
        <input
          type="date"
          aria-label="De"
          className={`${inputClass} mt-0 w-36`}
          value={dateFrom}
          onChange={(event) => setDateFrom(event.target.value)}
        />
        <input
          type="date"
          aria-label="Até"
          className={`${inputClass} mt-0 w-36`}
          value={dateTo}
          onChange={(event) => setDateTo(event.target.value)}
        />
        {canWrite && (
          <Button variant="primary" className="ml-auto" onClick={() => setCreating(true)}>
            Criar negócio
          </Button>
        )}
      </div>

      {query.isLoading ? (
        <Loading />
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState title="Nenhum negócio" />
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-400">
            <tr>
              <th className="py-2">Negócio</th>
              <th className="py-2">Produto</th>
              <th className="py-2">Etapa</th>
              <th className="py-2">Status</th>
              <th className="py-2 text-right">Valor</th>
              <th className="py-2 text-right">Data</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {query.data!.map((business) => (
              <tr key={business.id}>
                <td className="py-2">
                  <Link
                    to={`/negocios/${business.id}`}
                    className="text-sky-700 hover:underline"
                  >
                    #{business.code}
                  </Link>
                  {business.leadId !== lead.id && (
                    <span className="ml-1 text-xs text-slate-400">(empresa)</span>
                  )}
                </td>
                <td className="py-2 text-slate-600">
                  {business.products?.[0]?.product.name ?? 'Sem produto'}
                </td>
                <td className="py-2 text-slate-600">{business.stage?.name ?? '—'}</td>
                <td className="py-2">
                  <StatusBadge status={business.status} />
                </td>
                <td className="py-2 text-right">{formatMoney(business.total)}</td>
                <td className="py-2 text-right text-slate-500">
                  {formatDate(business.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {creating && (
        <LeadBusinessModal
          lead={lead}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            queryClient.invalidateQueries({ queryKey: ['lead-businesses', lead.id] });
            queryClient.invalidateQueries({ queryKey: ['lead', lead.id] });
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Atividades

export function ActivitiesSection({ lead }: { lead: Lead }) {
  const query = useQuery({
    queryKey: ['lead-activities', lead.id],
    queryFn: () => listLeadActivities(lead.id),
  });
  const refetch = () => void query.refetch();
  return (
    <div className="space-y-4">
      <ActivityComposer leadId={lead.id} onCreated={refetch} compact />
      {query.isLoading ? (
        <Loading />
      ) : (query.data ?? []).length === 0 ? (
        <EmptyState title="Nenhuma atividade" />
      ) : (
        <div className="space-y-2">
          {query.data!.map((activity) => (
            <ActivityRow
              key={activity.id}
              activity={activity}
              showBusinessLink
              onChanged={refetch}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Atendimentos (DC3): as conversas do lead no chat e cada atendimento delas. */
export function AttendancesSection({ lead }: { lead: Lead }) {
  const query = useQuery({
    queryKey: ['chat', 'by-lead', lead.id],
    queryFn: () => chatApi.byLead(lead.id),
  });
  if (query.isLoading) return <Loading />;
  const conversations = query.data ?? [];
  if (conversations.length === 0) {
    return (
      <EmptyState
        title="Nenhum atendimento"
        hint="As conversas do WhatsApp com o telefone deste lead aparecem aqui."
      />
    );
  }
  const minutes = (ms?: number) =>
    ms === undefined || ms === null
      ? '—'
      : ms < 60_000
        ? 'menos de 1min'
        : `${Math.round(ms / 60_000)}min`;
  return (
    <div className="space-y-3">
      {conversations.map((conversation) => (
        <div key={conversation._id} className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-slate-800">
              WhatsApp · {conversation.name ?? `+${conversation.contactId}`}
            </p>
            <Link
              to={`/chat?c=${conversation._id}`}
              className="text-xs text-slate-600 hover:underline"
            >
              Abrir no chat →
            </Link>
          </div>
          <p className="text-xs text-slate-500">
            Última mensagem {formatRelative(conversation.lastMessageDate)} ·{' '}
            {conversation.lastMessagePreview}
          </p>
          <ul className="mt-2 divide-y divide-slate-100 text-xs">
            {conversation.threads.map((thread) => (
              <li key={thread._id} className="flex flex-wrap justify-between gap-2 py-1.5">
                <span className="font-medium text-slate-700">Atendimento #{thread.code}</span>
                <span className="text-slate-500">
                  {formatDateTime(thread.startedAt)} · espera {minutes(thread.waitTimeInMs)} ·{' '}
                  {thread.finishedAt
                    ? `finalizado em ${minutes(thread.durationInMs)}`
                    : 'em aberto'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Históricos

const CATEGORY_LABELS: Array<[HistoryCategory | 'ALL', string]> = [
  ['ALL', 'Todos'],
  ['BUSINESS', 'Negócios'],
  ['LEAD', 'Lead'],
  ['ACTIVITY', 'Atividade'],
  ['COMMENT', 'Comentários'],
  ['CONVERSATION', 'Conversa'],
];

const CATEGORY_DOT: Record<HistoryCategory, string> = {
  LEAD: 'bg-sky-500',
  BUSINESS: 'bg-emerald-500',
  ACTIVITY: 'bg-amber-500',
  COMMENT: 'bg-violet-500',
  CONVERSATION: 'bg-slate-400',
};

export function HistorySection({ lead }: { lead: Lead }) {
  const queryClient = useQueryClient();
  const canWrite = useCanWrite();
  const [category, setCategory] = useState<HistoryCategory | 'ALL'>('ALL');
  const [comment, setComment] = useState('');
  const query = useQuery({
    queryKey: ['lead-history', lead.id, category],
    queryFn: () =>
      listLeadHistory(lead.id, undefined, category === 'ALL' ? undefined : category),
  });
  const add = useMutation({
    mutationFn: () => addLeadNote(lead.id, comment.trim()),
    onSuccess: () => {
      setComment('');
      queryClient.invalidateQueries({ queryKey: ['lead-history', lead.id] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <select
          className={`${inputClass} mt-0 w-44`}
          value={category}
          onChange={(event) => setCategory(event.target.value as HistoryCategory | 'ALL')}
        >
          {CATEGORY_LABELS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {canWrite && (
        <div className="flex gap-2">
          <input
            className={`${inputClass} mt-0`}
            placeholder="Escreva seu comentário..."
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
          <Button
            variant="primary"
            disabled={!comment.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            Enviar
          </Button>
        </div>
      )}
      {query.isLoading ? (
        <Loading />
      ) : (
        <ol className="space-y-2">
          {(query.data ?? []).map((entry) => (
            <li
              key={entry.id}
              className="flex gap-3 rounded-lg border border-slate-200 px-4 py-3 text-sm"
            >
              <span
                className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${CATEGORY_DOT[entry.category] ?? 'bg-slate-300'}`}
              />
              <div>
                <p className="text-slate-800">{entry.history ?? entry.note}</p>
                <p className="mt-1 text-[11px] text-slate-400">
                  {entry.authorType === 'AUTOMATION' ? 'Automação ' : ''}
                  {entry.authorName ?? 'Sistema'} · {formatDateTime(entry.createdAt)}
                </p>
              </div>
            </li>
          ))}
          {(query.data ?? []).length === 0 && (
            <li className="text-xs text-slate-400">Nenhum evento.</li>
          )}
        </ol>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Hierarquia

function TreeNode({ node, current }: { node: HierarchyNode; current: string }) {
  return (
    <li className="ml-4 border-l border-slate-200 pl-4">
      <p
        className={`text-sm ${node.id === current ? 'font-semibold text-slate-900' : 'text-slate-700'}`}
      >
        🏢 {node.name}
        {node.link && (
          <span className="ml-1 text-xs text-slate-400">
            ({node.link === 'BRANCH' ? 'filial' : 'controlada'})
          </span>
        )}
      </p>
      {node.employees.length > 0 && (
        <ul className="mb-1 ml-4 text-xs text-slate-500">
          {node.employees.map((employee) => (
            <li
              key={employee.id}
              className={employee.id === current ? 'font-semibold text-slate-800' : ''}
            >
              👤 {employee.name}
              {employee.jobTitle && ` · ${employee.jobTitle}`}
              {employee.isPrimary && ' · contato principal'}
            </li>
          ))}
        </ul>
      )}
      {node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <TreeNode key={child.id} node={child} current={current} />
          ))}
        </ul>
      )}
    </li>
  );
}

export function HierarchySection({ lead }: { lead: Lead }) {
  const query = useQuery({
    queryKey: ['lead-hierarchy', lead.id],
    queryFn: () => leadRelations.hierarchy(lead.id),
  });
  if (query.isLoading) return <Loading />;
  const roots = query.data?.roots ?? [];
  const empty =
    roots.length === 0 ||
    (roots.length === 1 && roots[0].children.length === 0 && roots[0].employees.length === 0);
  if (empty) {
    return (
      <EmptyState
        title="Sem hierarquia definida"
        hint={
          lead.kind === 'COMPANY'
            ? 'Vincule empresas na seção Empresas.'
            : 'Vincule a pessoa a uma empresa na seção Empresas.'
        }
      />
    );
  }
  return (
    <ul className="-ml-4">
      {roots.map((root) => (
        <TreeNode key={root.id} node={root} current={lead.id} />
      ))}
    </ul>
  );
}

// ------------------------------------------------------- Pessoas e Empresas

const RELATION_LABELS: Record<RelationKind, string> = {
  EMPLOYEE: 'Funcionário(a)',
  PARENT_COMPANY: 'Matriz',
  BRANCH: 'Filial',
  HOLDING: 'Holding',
  SUBSIDIARY: 'Controlada',
  GROUP: 'Mesmo grupo',
  PARTNER: 'Parceira',
};

function useRelations(lead: Lead) {
  const queryClient = useQueryClient();
  const refresh = useLeadRefresh(lead.id);
  const query = useQuery({
    queryKey: ['lead-relations', lead.id],
    queryFn: () => leadRelations.list(lead.id),
  });
  const invalidate = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ['lead-hierarchy'] });
    queryClient.invalidateQueries({ queryKey: ['lead'] });
  };
  return { query, invalidate };
}

export function PeopleSection({ lead }: { lead: Lead }) {
  const canWrite = useCanWrite();
  const { query, invalidate } = useRelations(lead);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const primary = useMutation({
    mutationFn: (relationId: string) => leadRelations.setPrimary(lead.id, relationId),
    onSuccess: invalidate,
  });
  const unlink = useMutation({
    mutationFn: (relationId: string) => leadRelations.remove(lead.id, relationId),
    onSuccess: () => {
      setUnlinking(null);
      invalidate();
    },
  });

  if (lead.kind === 'PERSON') {
    return (
      <EmptyState
        title="Pessoas vinculadas ficam no lead Empresa"
        hint="Use a seção Empresas para ligar esta pessoa a uma empresa."
      />
    );
  }
  const people = query.data?.people ?? [];

  return (
    <div className="space-y-3">
      {canWrite && (
        <Button variant="primary" onClick={() => setLinking(true)}>
          + Vincular pessoa
        </Button>
      )}
      {query.isLoading ? (
        <Loading />
      ) : people.length === 0 ? (
        <EmptyState
          title="Nenhuma pessoa vinculada"
          hint="Vincule pessoas para estruturar o relacionamento."
        />
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {people.map((item) => (
            <li key={item.relationId} className="flex items-center gap-3 px-4 py-3 text-sm">
              <span className="min-w-0 flex-1">
                <span className="font-medium text-slate-800">{item.lead.name}</span>
                {item.isPrimary && (
                  <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
                    Contato principal
                  </span>
                )}
                <span className="block text-xs text-slate-400">
                  {[item.jobTitle, item.lead.email, item.lead.phone]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </span>
              {canWrite && !item.isPrimary && (
                <Button variant="ghost" onClick={() => primary.mutate(item.relationId)}>
                  Tornar principal
                </Button>
              )}
              {canWrite && (
                <Button variant="ghost" onClick={() => setUnlinking(item.relationId)}>
                  Desvincular
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {linking && (
        <LinkModal
          lead={lead}
          mode="person"
          onClose={() => setLinking(false)}
          onDone={invalidate}
        />
      )}
      {unlinking && (
        <ConfirmDialog
          title="Desvincular?"
          message="O vínculo sai dos dois leads. Os leads continuam."
          confirmLabel="Desvincular"
          danger
          busy={unlink.isPending}
          onConfirm={() => unlink.mutate(unlinking)}
          onClose={() => setUnlinking(null)}
        />
      )}
    </div>
  );
}

export function CompaniesSection({ lead }: { lead: Lead }) {
  const canWrite = useCanWrite();
  const { query, invalidate } = useRelations(lead);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const unlink = useMutation({
    mutationFn: (relationId: string) => leadRelations.remove(lead.id, relationId),
    onSuccess: () => {
      setUnlinking(null);
      invalidate();
    },
  });
  const companies = query.data?.companies ?? [];

  return (
    <div className="space-y-3">
      {canWrite && (
        <Button variant="primary" onClick={() => setLinking(true)}>
          + Vincular empresa
        </Button>
      )}
      {query.isLoading ? (
        <Loading />
      ) : companies.length === 0 ? (
        <EmptyState
          title="Sem empresa vinculada"
          hint={
            lead.kind === 'COMPANY'
              ? 'Vincule a uma holding, grupo, matriz ou parceiro.'
              : 'Vincule a pessoa à empresa onde trabalha.'
          }
        />
      ) : (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {companies.map((item) => (
            <li key={item.relationId} className="flex items-center gap-3 px-4 py-3 text-sm">
              <span className="min-w-0 flex-1">
                <span className="font-medium text-slate-800">{item.lead.name}</span>
                <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                  {RELATION_LABELS[item.kind]}
                </span>
                <span className="block text-xs text-slate-400">
                  {[item.lead.tradeName, item.jobTitle, item.lead.taxId]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </span>
              {canWrite && (
                <Button variant="ghost" onClick={() => setUnlinking(item.relationId)}>
                  Desvincular
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {linking && (
        <LinkModal
          lead={lead}
          mode="company"
          onClose={() => setLinking(false)}
          onDone={invalidate}
        />
      )}
      {unlinking && (
        <ConfirmDialog
          title="Desvincular?"
          message="O vínculo sai dos dois leads. Os leads continuam."
          confirmLabel="Desvincular"
          danger
          busy={unlink.isPending}
          onConfirm={() => unlink.mutate(unlinking)}
          onClose={() => setUnlinking(null)}
        />
      )}
    </div>
  );
}

/** "Vincular pessoa" / "Vincular empresa". */
function LinkModal({
  lead,
  mode,
  onClose,
  onDone,
}: {
  lead: Lead;
  mode: 'person' | 'company';
  onClose: () => void;
  onDone: () => void;
}) {
  const [target, setTarget] = useState<{ id: string; name: string } | null>(null);
  const companyToCompany = mode === 'company' && lead.kind === 'COMPANY';
  const [kind, setKind] = useState<RelationKind>(
    companyToCompany ? 'PARENT_COMPANY' : 'EMPLOYEE',
  );
  const [jobTitle, setJobTitle] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      leadRelations.create(lead.id, {
        targetId: target!.id,
        kind,
        jobTitle: kind === 'EMPLOYEE' && jobTitle.trim() ? jobTitle.trim() : undefined,
        isPrimary: kind === 'EMPLOYEE' ? isPrimary : undefined,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Modal title={mode === 'person' ? 'Vincular pessoa' : 'Vincular empresa'} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          {mode === 'person'
            ? 'Esta pessoa será vinculada como funcionária da empresa.'
            : companyToCompany
              ? 'Defina o relacionamento entre as empresas.'
              : 'A pessoa será vinculada como funcionária desta empresa.'}
        </p>
        <Field label={mode === 'person' ? 'Pessoa *' : 'Empresa *'}>
          <LeadPicker
            kind={mode === 'person' ? 'PERSON' : 'COMPANY'}
            value={target}
            onChange={setTarget}
            excludeIds={[lead.id]}
            placeholder={
              mode === 'person' ? 'Buscar por nome ou e-mail' : 'Buscar por nome ou CNPJ'
            }
          />
        </Field>
        {companyToCompany && (
          <Field label="Relação">
            <select
              className={inputClass}
              value={kind}
              onChange={(event) => setKind(event.target.value as RelationKind)}
            >
              <option value="PARENT_COMPANY">É a matriz desta empresa</option>
              <option value="BRANCH">É filial desta empresa</option>
              <option value="HOLDING">É a holding desta empresa</option>
              <option value="GROUP">Mesmo grupo</option>
              <option value="PARTNER">Parceira</option>
            </select>
          </Field>
        )}
        {kind === 'EMPLOYEE' && (
          <>
            <Field label="Cargo na empresa">
              <input
                className={inputClass}
                value={jobTitle}
                onChange={(event) => setJobTitle(event.target.value)}
              />
            </Field>
            <Toggle
              label="Contato principal da empresa"
              checked={isPrimary}
              onChange={setIsPrimary}
            />
          </>
        )}
        {create.error && (
          <ErrorNote>{errorMessage(create.error, 'Não foi possível vincular')}</ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!target || create.isPending}
            onClick={() => create.mutate()}
          >
            Vincular
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- Endereços

const ADDRESS_TYPES: Array<[LeadAddressType, string]> = [
  ['MAIN', 'Matriz'],
  ['BILLING', 'Faturamento'],
  ['CHARGE', 'Cobrança'],
  ['DELIVERY', 'Entrega'],
];

export function AddressesSection({ lead }: { lead: Lead }) {
  const canWrite = useCanWrite();
  const refresh = useLeadRefresh(lead.id);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['lead-addresses', lead.id],
    queryFn: () => leadAddresses.list(lead.id),
  });
  const [editing, setEditing] = useState<LeadAddressRow | 'new' | null>(null);
  const [removing, setRemoving] = useState<LeadAddressRow | null>(null);
  const invalidate = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ['lead-addresses', lead.id] });
  };
  const remove = useMutation({
    mutationFn: (row: LeadAddressRow) => leadAddresses.remove(lead.id, row.id),
    onSuccess: () => {
      setRemoving(null);
      invalidate();
    },
  });
  const rows = query.data ?? [];
  const freeTypes = ADDRESS_TYPES.filter(([type]) => !rows.some((row) => row.type === type));

  return (
    <div className="space-y-3">
      {canWrite && freeTypes.length > 0 && (
        <Button variant="primary" onClick={() => setEditing('new')}>
          + Adicionar endereço
        </Button>
      )}
      {query.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <EmptyState title="Nenhum endereço cadastrado" />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {rows.map((row) => (
            <div key={row.id} className="rounded-xl border border-slate-200 p-4 text-sm">
              <p className="mb-1 text-xs font-semibold uppercase text-slate-400">
                {ADDRESS_TYPES.find(([type]) => type === row.type)?.[1]}
              </p>
              <p className="text-slate-700">
                {[row.address, row.number, row.complement].filter(Boolean).join(', ') || '—'}
              </p>
              <p className="text-slate-500">
                {[row.block, row.city, row.state, row.zip, row.country]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {canWrite && (
                <div className="mt-2 flex gap-3 text-xs">
                  <button
                    type="button"
                    className="text-slate-500 hover:text-slate-900"
                    onClick={() => setEditing(row)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-red-600"
                    onClick={() => setRemoving(row)}
                  >
                    Excluir
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {editing && (
        <AddressModal
          leadId={lead.id}
          row={editing === 'new' ? null : editing}
          types={
            editing === 'new'
              ? freeTypes
              : ADDRESS_TYPES.filter(
                  ([type]) =>
                    type === (editing as LeadAddressRow).type ||
                    freeTypes.some(([free]) => free === type),
                )
          }
          onClose={() => setEditing(null)}
          onDone={invalidate}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Excluir endereço?"
          message="O endereço sai da ficha (não vai para a Lixeira)."
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

function AddressModal({
  leadId,
  row,
  types,
  onClose,
  onDone,
}: {
  leadId: string;
  row: LeadAddressRow | null;
  types: Array<[LeadAddressType, string]>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    type: row?.type ?? types[0]?.[0] ?? 'MAIN',
    country: row?.country ?? 'BR',
    zip: row?.zip ?? '',
    address: row?.address ?? '',
    number: row?.number ?? '',
    complement: row?.complement ?? '',
    block: row?.block ?? '',
    city: row?.city ?? '',
    state: row?.state ?? '',
  });
  const save = useMutation({
    mutationFn: () =>
      row ? leadAddresses.update(leadId, row.id, form) : leadAddresses.create(leadId, form),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const fieldsList = useMemo(
    () =>
      [
        ['country', 'País', 2],
        ['zip', 'CEP', 12],
        ['address', 'Endereço', 200],
        ['number', 'Número', 20],
        ['complement', 'Complemento', 120],
        ['block', 'Bairro', 120],
        ['city', 'Cidade', 120],
        ['state', 'UF', 60],
      ] as const,
    [],
  );

  return (
    <Modal title={row ? 'Editar endereço' : 'Adicionar endereço'} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Tipo">
          <select
            className={inputClass}
            value={form.type}
            onChange={(event) =>
              setForm({ ...form, type: event.target.value as LeadAddressType })
            }
          >
            {types.map(([type, label]) => (
              <option key={type} value={type}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          {fieldsList.map(([key, label, max]) => (
            <Field key={key} label={label}>
              <input
                maxLength={max}
                className={inputClass}
                value={form[key]}
                onChange={(event) =>
                  setForm({
                    ...form,
                    [key]:
                      key === 'country' ? event.target.value.toUpperCase() : event.target.value,
                  })
                }
              />
            </Field>
          ))}
        </div>
        {save.error && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Salvar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------- Arquivos e Notas

export function FilesSection({ lead }: { lead: Lead }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        O upload de arquivos é feito para {lead.name}, o lead em exibição.
      </p>
      <BusinessFiles leadId={lead.id} />
    </div>
  );
}

export function NotesSection({ lead }: { lead: Lead }) {
  const canWrite = useCanWrite();
  const save = useSaveField(lead);
  const [notes, setNotes] = useState(lead.notes ?? '');
  const [saved, setSaved] = useState(false);
  const mutation = useMutation({
    mutationFn: () => save({ notes: notes.trim() ? notes : null }),
    onSuccess: () => setSaved(true),
  });

  return (
    <div className="space-y-2">
      <textarea
        rows={14}
        maxLength={50000}
        disabled={!canWrite}
        className={inputClass}
        placeholder="Escreva algumas notas..."
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value);
          setSaved(false);
        }}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{notes.length} / 50.000 caracteres</span>
        <div className="flex items-center gap-3">
          {saved && <span className="text-xs text-emerald-600">Salvo.</span>}
          {mutation.error && (
            <span className="text-xs text-red-600">{errorMessage(mutation.error)}</span>
          )}
          {canWrite && (
            <Button
              variant="primary"
              disabled={mutation.isPending || notes === (lead.notes ?? '')}
              onClick={() => mutation.mutate()}
            >
              Salvar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
